import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface Product {
  link: string;
  title: string;
  icon: string;
  tab_id: string;
  i18n_key?: string;
  pop_out?: boolean;
  ng_route: boolean;
  svg_icon: boolean;
  right_icon?: string;
  nested_tabs: Product[];
  locked: boolean;
  subsection_header?: string;
  badge?: string;
}

export class AppConfig {
  customizeWhitelabelUrl: string;
  userOptions: { label: string; url: string; menuId: string }[];
  products: Product[];
  partnerId: string;
  partnerName: string;
  userIsAdmin: boolean;
  userCanAccessAccounts: boolean;
  userCanAccessAutomations: boolean;
  userCanAccessBilling: boolean;
  userCanAccessCompanyProfile: boolean;
  userCanAccessDashboard: boolean;
  userCanAccessMarketing: boolean;
  userCanAccessRetailBilling: boolean;
  userCanAccessSnapshotWidget: boolean;
  userCanCustomizeWhitelabel: boolean;
  userCanAccessOrders: boolean;
  userCanAccessSales: boolean;
  canEnableApps: boolean;
  googleAnalyticsDimensions: string[];
  googleAnalyticsCode: string;
  // @deprecated: this can be an email, use unifiedUserId instead
  userId?: string;
  unifiedUserId?: string;
  userEmail?: string;
  userFirstName?: string;
  userLastName?: string;
  userMarket?: string;
  partnerCreated?: number;
  userCreated?: number;
  gtmAuth?: string;
  gtmPreview?: string;
  gtmContainer?: string;
  supportEnabled?: boolean;
  hotjarTrackingCode?: string;
  wootricEnabled?: boolean;
  accountExecutive?: string;
  accountExecutiveSlack?: string;
  acceptedTermsAndConditions?: boolean;
  isSuperAdmin?: boolean;
  isImpersonated?: boolean;
  sessionId?: string;
  isBeta?: boolean;
  accessibleMarketIds?: string[];
  environment?: 'test' | 'demo' | 'prod' | 'local';
  partnerOrigin?: string;
  selectedTab: string;
  hasFailedPurchase: boolean;
  affiliateApproved?: boolean;
}

export interface AppConfigProvider {
  config$: Observable<AppConfig>;
}

const APP_CONFIG_URL = '/api/v1/app-config';

@Injectable({ providedIn: 'root' })
export class AppConfigService implements AccessibleMarketsProvider, AppConfigProvider {
  private readonly config$$: BehaviorSubject<AppConfig> = new BehaviorSubject(null);
  private readonly loading$$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public readonly loading$ = this.loading$$.asObservable();

  public get config$(): Observable<AppConfig> {
    return this.config$$.asObservable().pipe(
      filter((config) => {
        return !!config;
      }),
    );
  }

  public get userAccessibleMarkets$(): Observable<string[]> {
    return this.config$.pipe(map((config) => config.accessibleMarketIds));
  }

  public get isImpersonated(): boolean {
    return this.config.isImpersonated;
  }

  public get config(): AppConfig {
    return this.config$$.getValue();
  }

  public set config(value: AppConfig) {
    this.config$$.next(value);
  }

  constructor(private http: HttpClient) {}

  public async load(partnerId?: string): Promise<boolean> {
    try {
      this.loading$$.next(true);
      if (partnerId) {
        // To get the app config, the backend currently heavily relies on the session cookie for the partner ID, which
        // makes it difficult to switch the partner ID on the same request as wanting to use the new partner ID.
        // This post request will update the session cookie first so that the next call can use the session cookie with
        // the new partner ID in it.
        await firstValueFrom(
          this.http.post(`${APP_CONFIG_URL}?_pid=${partnerId.toUpperCase()}`, { withCredentials: true }),
        );
      }
      const result = await firstValueFrom(this.http.get<AppConfig>(APP_CONFIG_URL, { withCredentials: true }));
      this.config$$.next({ ...result, affiliateApproved: false }); // TODO: replace static affiliate value with a call to backend
    } catch (error) {
      if (window.location.pathname !== '/login') {
        window.history.replaceState({}, 'Partner Login', '/login');
      }
    } finally {
      this.loading$$.next(false);
    }
    return true;
  }

  public excludeSpecificPIDForSSC(): boolean {
    const pid = this.config.partnerId;
    return pid === 'VMF' || pid === 'ABC';
  }
}

/**
 * @deprecated userAccessibleMarkets should be moved to MarketsServiceInterface.
 */
export interface AccessibleMarketsProvider {
  userAccessibleMarkets$: Observable<string[]>;
  isImpersonated: boolean;
}
